import { Link } from "@mui/material";
import { CSSProperties } from "react";
import { DialogContent } from "../components/DialogContent";

const sxUl: CSSProperties = {
  padding: "0 16px",
  listStyleType: "circle",
};

type Props = {
  isTransparent?: boolean;
  onClose: () => void;
  tooltipCloseButton?: string;
};

export function Terms(props: Props) {
  const { isTransparent, onClose, tooltipCloseButton } = props;
  return (
    <DialogContent
      isTransparent={isTransparent}
      tooltipCloseButton={tooltipCloseButton ?? `Close "Privacy policy" dialog`}
      onClose={onClose}
    >
      <h1>Terms and Conditions</h1>
      <p>
        These Terms and Conditions ("<strong>Terms</strong>") govern your access
        to and use of the iulian-radu.eu website (the "
        <strong>Website</strong>") and any Android, iOS or other applications
        published by us (the "<strong>Apps</strong>", and together with the
        Website, the "<strong>Services</strong>"). By accessing or using the
        Services you agree to be bound by these Terms.{' '}
        <strong style={{ color: 'red' }}>
          If you do not agree with these Terms, do not access or use the
          Website, the Apps or any other aspect of our business.
        </strong>
      </p>
      <p>
        When we refer to "<strong>we</strong>", "<strong>us</strong>" or "
        <strong>our</strong>", we mean the owner of iulian-radu.eu and of the
        Apps. When we refer to "<strong>you</strong>" or "<strong>User</strong>
        ", we mean any individual or entity that accesses or uses the Services.
      </p>

      <h2>Table of Contents:</h2>
      <ul style={{ ...sxUl, marginTop: -16 }}>
        <li>
          <Link href="#acceptance">Acceptance of the Terms</Link>
        </li>
        <li>
          <Link href="#eligibility">Eligibility</Link>
        </li>
        <li>
          <Link href="#license">License to Use the Services</Link>
        </li>
        <li>
          <Link href="#accounts">User Accounts</Link>
        </li>
        <li>
          <Link href="#conduct">Acceptable Use</Link>
        </li>
        <li>
          <Link href="#content">User Content</Link>
        </li>
        <li>
          <Link href="#ip">Intellectual Property</Link>
        </li>
        <li>
          <Link href="#third-party">Third Party Services and Links</Link>
        </li>
        <li>
          <Link href="#fees">Fees and Payments</Link>
        </li>
        <li>
          <Link href="#disclaimer">Disclaimer of Warranties</Link>
        </li>
        <li>
          <Link href="#liability">Limitation of Liability</Link>
        </li>
        <li>
          <Link href="#indemnification">Indemnification</Link>
        </li>
        <li>
          <Link href="#termination">Suspension and Termination</Link>
        </li>
        <li>
          <Link href="#privacy">Privacy</Link>
        </li>
        <li>
          <Link href="#changes">Changes to the Services and the Terms</Link>
        </li>
        <li>
          <Link href="#law">Governing Law and Dispute Resolution</Link>
        </li>
        <li>
          <Link href="#misc">Miscellaneous</Link>
        </li>
        <li>
          <Link href="#contact">Contacting Us</Link>
        </li>
      </ul>

      <h2 id="acceptance">Acceptance of the Terms</h2>
      <p>
        By accessing the Website, downloading or installing any of the Apps, or
        otherwise using the Services, you confirm that you have read,
        understood and agree to be bound by these Terms, together with any
        additional terms, policies and notices that we may publish from time to
        time. These Terms form a legally binding agreement between you and us.
      </p>

      <h2 id="eligibility">Eligibility</h2>
      <p>
        To the extent prohibited by applicable law, the Services may not be
        used by anyone younger than 18 years old. By using the Services you
        represent and warrant that you are at least 18 years of age and have
        the legal capacity to enter into these Terms. If you are using the
        Services on behalf of an organization, you represent and warrant that
        you are authorized to bind that organization to these Terms.
      </p>

      <h2 id="license">License to Use the Services</h2>
      <p>
        Subject to your compliance with these Terms, we grant you a limited,
        personal, non-exclusive, non-transferable, non-sublicensable and
        revocable license to access and use the Services for your own
        non-commercial use. All rights not expressly granted to you in these
        Terms are reserved by us and our licensors.
      </p>
      <p>You agree that you will not, and will not permit any third party to:</p>
      <ul style={sxUl}>
        <li>
          copy, modify, adapt, translate or create derivative works of the
          Services or any part of them;
        </li>
        <li>
          reverse engineer, decompile, disassemble or otherwise attempt to
          discover the source code or underlying algorithms of the Services,
          except to the extent such restriction is prohibited by applicable
          law;
        </li>
        <li>
          rent, lease, sell, sublicense, assign, distribute, publish, transfer
          or otherwise make the Services available to any third party;
        </li>
        <li>
          remove or alter any proprietary notices, labels or marks on or in
          the Services.
        </li>
      </ul>

      <h2 id="accounts">User Accounts</h2>
      <p>
        Some features of the Services may require you to register an account.
        When you create an account, you agree to provide accurate, current and
        complete information and to keep that information up to date. You are
        responsible for safeguarding your credentials and for any activity
        that occurs under your account. You agree to notify us immediately of
        any unauthorized use of your account or any other breach of security.
        We are not liable for any loss or damage arising from your failure to
        keep your credentials secure.
      </p>

      <h2 id="conduct">Acceptable Use</h2>
      <p>
        You agree to use the Services only for lawful purposes and in a manner
        that does not infringe the rights of, restrict or inhibit anyone
        else's use and enjoyment of the Services. In particular, you agree not
        to:
      </p>
      <ul style={sxUl}>
        <li>
          use the Services in violation of any applicable law, regulation or
          third party rights;
        </li>
        <li>
          upload, post, transmit or otherwise make available any content that
          is unlawful, harmful, threatening, abusive, harassing, defamatory,
          vulgar, obscene, invasive of another's privacy, hateful or otherwise
          objectionable;
        </li>
        <li>
          impersonate any person or entity, or falsely state or otherwise
          misrepresent your affiliation with a person or entity;
        </li>
        <li>
          interfere with or disrupt the Services or servers or networks
          connected to the Services, including by transmitting viruses,
          worms, Trojan horses or any other harmful code;
        </li>
        <li>
          attempt to gain unauthorized access to any portion or feature of the
          Services, other accounts, computer systems or networks connected to
          the Services, whether through hacking, password mining or any other
          means;
        </li>
        <li>
          use any robot, spider, scraper or other automated means to access
          the Services for any purpose without our express written
          permission;
        </li>
        <li>
          use the Services to send unsolicited communications, promotions,
          advertising or spam.
        </li>
      </ul>

      <h2 id="content">User Content</h2>
      <p>
        You retain ownership of any content that you submit, post or display
        on or through the Services ("<strong>User Content</strong>"). By
        submitting User Content you grant us a worldwide, non-exclusive,
        royalty-free, transferable and sublicensable license to use,
        reproduce, modify, adapt, publish, translate, distribute and display
        such User Content solely to the extent necessary to operate, provide
        and improve the Services.
      </p>
      <p>
        You are solely responsible for your User Content and the consequences
        of submitting it. You represent and warrant that you own or have the
        necessary rights, licenses, consents and permissions to grant the
        license set out above and that your User Content does not infringe or
        violate the rights of any third party.
      </p>

      <h2 id="ip">Intellectual Property</h2>
      <p>
        The Services and all materials contained therein, including but not
        limited to software, source code, text, graphics, logos, icons,
        images, audio and video clips and the selection and arrangement
        thereof, are the property of us or our licensors and are protected by
        copyright, trademark and other intellectual property laws. Except for
        the limited license expressly granted to you in these Terms, nothing
        in these Terms transfers any right, title or interest in or to the
        Services to you.
      </p>

      <h2 id="third-party">Third Party Services and Links</h2>
      <p>
        The Services may contain links to, or be used in conjunction with,
        third party websites, applications or services that are not owned or
        controlled by us ("<strong>Third Party Services</strong>"). We have no
        control over, and assume no responsibility for, the content, privacy
        policies or practices of any Third Party Services. You acknowledge and
        agree that we are not responsible or liable, directly or indirectly,
        for any damage or loss caused or alleged to be caused by or in
        connection with the use of or reliance on any such Third Party
        Services.
      </p>

      <h2 id="fees">Fees and Payments</h2>
      <p>
        Some portions of the Services may be offered for a fee. Where fees
        apply, the applicable pricing, billing cycle and payment terms will be
        presented to you before you complete the relevant transaction. Unless
        expressly stated otherwise, all fees are non-refundable. You are
        responsible for any taxes, duties or other governmental charges
        applicable to your purchase. We reserve the right to change our fees
        at any time, with such changes taking effect upon your next billing
        cycle.
      </p>

      <h2 id="disclaimer">Disclaimer of Warranties</h2>
      <p>
        <strong>
          THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS,
          WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
        </strong>{' '}
        To the maximum extent permitted by applicable law, we disclaim all
        warranties, express or implied, including, but not limited to,
        implied warranties of merchantability, fitness for a particular
        purpose, title and non-infringement. We do not warrant that the
        Services will be uninterrupted, secure or error-free, that defects
        will be corrected, or that the Services or the servers that make them
        available are free of viruses or other harmful components.
      </p>

      <h2 id="liability">Limitation of Liability</h2>
      <p>
        <strong>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
          SHALL WE, OUR AFFILIATES, OR OUR LICENSORS BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES,
          OR ANY LOSS OF PROFITS, REVENUES, DATA, USE, GOODWILL OR OTHER
          INTANGIBLE LOSSES, RESULTING FROM
        </strong>{' '}
        (i) your access to or use of, or inability to access or use, the
        Services; (ii) any conduct or content of any third party on the
        Services; or (iii) unauthorized access, use or alteration of your
        transmissions or User Content, whether based on warranty, contract,
        tort (including negligence) or any other legal theory, whether or not
        we have been informed of the possibility of such damage. Nothing in
        these Terms shall limit or exclude any liability that cannot be
        limited or excluded under applicable law.
      </p>

      <h2 id="indemnification">Indemnification</h2>
      <p>
        You agree to defend, indemnify and hold us, our affiliates, and our
        respective officers, directors, employees and agents harmless from
        and against any claims, liabilities, damages, losses and expenses,
        including, without limitation, reasonable legal and accounting fees,
        arising out of or in any way connected with (i) your access to or use
        of the Services; (ii) your User Content; (iii) your violation of
        these Terms; or (iv) your violation of any rights of another.
      </p>

      <h2 id="termination">Suspension and Termination</h2>
      <p>
        We may suspend or terminate your access to all or any part of the
        Services at any time, with or without cause, with or without notice,
        effective immediately, including if we believe in our sole discretion
        that you have violated these Terms. Upon termination, your right to
        use the Services will immediately cease. All provisions of these
        Terms which by their nature should survive termination shall survive
        termination, including, without limitation, ownership provisions,
        warranty disclaimers, indemnity and limitations of liability.
      </p>

      <h2 id="privacy">Privacy</h2>
      <p>
        Your use of the Services is also governed by our Privacy Policy,
        which describes how we collect, use and disclose information about
        you. By using the Services, you consent to the collection and use of
        information as described in the Privacy Policy.
      </p>

      <h2 id="changes">Changes to the Services and the Terms</h2>
      <p>
        We reserve the right to modify, suspend or discontinue, temporarily
        or permanently, the Services or any part thereof, with or without
        notice and without liability to you. We may also revise these Terms
        from time to time. The most current version will always be posted on
        the Website. By continuing to access or use the Services after
        revisions become effective, you agree to be bound by the revised
        Terms. If you do not agree to the new Terms, you must stop using the
        Services.
      </p>

      <h2 id="law">Governing Law and Dispute Resolution</h2>
      <p>
        These Terms and any dispute or claim arising out of or in connection
        with them or their subject matter or formation (including
        non-contractual disputes or claims) shall be governed by and
        construed in accordance with the laws of Austria, without regard to
        its conflict of law provisions. You agree to submit to the exclusive
        jurisdiction of the competent courts of Vienna, Austria for the
        resolution of any such dispute, subject to any mandatory consumer
        protection rights that may apply to you under the law of your country
        of residence.
      </p>

      <h2 id="misc">Miscellaneous</h2>
      <p>
        These Terms constitute the entire agreement between you and us
        regarding the Services and supersede any prior agreements between you
        and us relating to the Services. If any provision of these Terms is
        held to be invalid or unenforceable, the remaining provisions shall
        remain in full force and effect. Our failure to exercise or enforce
        any right or provision of these Terms shall not constitute a waiver
        of such right or provision. You may not assign or transfer these
        Terms, by operation of law or otherwise, without our prior written
        consent. Any attempt by you to assign or transfer these Terms,
        without such consent, will be null and void.
      </p>

      <h2 id="contact">Contacting Us</h2>
      <p>
        If you have any questions about these Terms, please contact us at{' '}
        <Link href="mailto:iulian.radu@gmx.at?subject=Question about Terms and Conditions">
          iulian.radu@gmx.at
        </Link>
        .
      </p>
    </DialogContent>
  );
}
